import { Project } from "projen/lib";
import { AgentRuleBundle } from "../agent-config-options";
import { AGENT_RULE_SCOPE } from "../types";
import { findProjectsWithDep, hasDep } from "./utils";

/**
 * AWS CDK bundle — auto-detected when `aws-cdk-lib` is in dependencies.
 */
export const awsCdkBundle: AgentRuleBundle = {
  name: "aws-cdk",
  description:
    "AWS CDK construct patterns, L2/L3 conventions, IAM best practices",
  appliesWhen: (project: Project) => hasDep(project, "aws-cdk-lib"),
  findApplicableProjects: (project: Project) =>
    findProjectsWithDep(project, "aws-cdk-lib"),
  rules: [
    {
      name: "aws-cdk-conventions",
      description: "AWS CDK construct patterns and best practices",
      scope: AGENT_RULE_SCOPE.FILE_PATTERN,
      filePatterns: ["**/*.ts"],
      content: [
        "# AWS CDK Conventions",
        "",
        "## Construct Structure",
        "",
        "- All constructs extend `Construct` from `constructs`",
        "- Use `readonly` for all props interfaces",
        "- Include minimal JSDoc for configuration options",
        "- Follow AWS CDK best practices for resource naming and organization",
        "- Use proper TypeScript types from `aws-cdk-lib`",
        "- Export constructs from `index.ts` for public API",
        "",
        "## CDK Construct Pattern",
        "",
        "```typescript",
        "export interface MyConstructProps {",
        "  /** Brief description. */",
        "  readonly myProperty: string;",
        "}",
        "",
        "export class MyConstruct extends Construct {",
        "  constructor(scope: Construct, id: string, props: MyConstructProps) {",
        "    super(scope, id);",
        "    // Implementation",
        "  }",
        "}",
        "```",
        "",
        "## L2 Over L1 Construct Preference",
        "",
        "- Always prefer L2 constructs (e.g., `s3.Bucket`, `lambda.Function`) over L1 escape hatches (`CfnBucket`, `CfnFunction`)",
        "- Only drop to L1 when the L2 construct is missing the feature you need or does not exist yet",
        "- When using L1, add a comment explaining why the L2 was insufficient",
        "",
        "## Filename / Class Name Matching",
        "",
        "- Kebab-case filenames must mirror PascalCase class names (e.g., `my-construct.ts` → `MyConstruct`)",
        "- Name props interfaces `ClassNameProps` (e.g., `MyConstructProps`)",
        "- One primary construct per file; supporting types (props interface, enums) live in the same file",
        "- Test files follow the same pattern: `my-construct.spec.ts`",
        "- When renaming, prefer renaming the class to match the filename",
        "",
        "## Cross-Stack Lookup Patterns",
        "",
        "- Lookups live on **service classes**, not component/construct classes — do not add `fromConstruct()` or similar lookup methods to component classes",
        "- Use `*.of(this)` for stack or service references where the pattern exists in the codebase",
        "- Within services, prefer creating resources in protected methods; expose retrieval via static methods on the owning service",
        "- Use static methods for cross-stack lookups so consumers don't need an instance:",
        "",
        "```typescript",
        "export class MyService {",
        "  /** Look up a resource from another stack via SSM. */",
        "  public static lookupArn(scope: Construct, id: string): string {",
        "    return StringParameter.valueForStringParameter(",
        "      scope,",
        "      `/my-app/my-resource-arn`,",
        "    );",
        "  }",
        "}",
        "```",
        "",
        "- Store outputs in SSM parameters with well-known paths; do not pass values between stacks via props",
        "",
        "## AWS Best Practices",
        "",
        "- Use AWS CDK v2 (`aws-cdk-lib`)",
        "- Follow AWS best practices for security and resource configuration",
        "- Use proper IAM permissions (principle of least privilege)",
        "- Include proper tags and descriptions for resources",
        "- Use SSM parameters for cross-stack references when needed",
        "- Do not pass values between stacks; use SSM parameters instead",
        "",
        "## Lambda Handlers (NodejsFunction)",
        "",
        "- When adding a `NodejsFunction` with a bundled handler, ensure the `entry` path is explicitly configured",
        "- Verify the entry is included in the build tool config (e.g., tsup entry list) so the runtime can find the handler",
        "",
        "## Lambda Construct Encapsulation",
        "",
        "When a Lambda construct is event-driven, it should own:",
        "",
        "- The EventBridge Rule (or other trigger) that routes events to itself.",
        "- The IAM permissions it needs on resources passed in via props.",
        "",
        "Pass the resources (event bus, table, queue, etc.) into the Lambda",
        "construct's props rather than defining triggers and grants in the",
        "enclosing workflow or service. The Lambda construct then becomes",
        'self-describing — "what triggers me, what I write to" lives in one',
        "file — and reverting or refactoring a single Lambda doesn't ripple",
        "through unrelated constructs.",
        "",
        "```typescript",
        "export interface MyLambdaProps {",
        "  readonly eventBus: IEventBus;",
        "  readonly dataStoreTable: ITable;",
        "}",
        "",
        "export class MyLambda extends Construct {",
        "  public readonly lambda: NodejsFunction;",
        "  public readonly rule: Rule;",
        "",
        "  constructor(scope: Construct, props: MyLambdaProps) {",
        '    super(scope, "my-lambda");',
        "",
        '    this.lambda = new NodejsFunction(this, "handler", { /* ... */ });',
        "    props.dataStoreTable.grantReadWriteData(this.lambda);",
        '    this.rule = new Rule(this, "rule", {',
        "      eventBus: props.eventBus,",
        "      eventPattern: { /* ... */ },",
        "      targets: [new LambdaFunction(this.lambda)],",
        "    });",
        "  }",
        "}",
        "```",
        "",
        "A workflow or service construct then composes these Lambda constructs",
        "without owning their triggers or grants directly.",
        "",
        "## CDK Testing",
        "",
        "- Mock `Code.fromAsset` in any test file that synthesizes CDK stacks",
        "- Use static S3 values (`mock-assets-bucket`, `mock-asset-key.zip`) so snapshots are stable",
        "- Add the mock in `beforeAll` and restore in `afterAll`:",
        "",
        "```typescript",
        "let fromAssetMock: MockInstance;",
        "",
        "beforeAll(() => {",
        '  fromAssetMock = vi.spyOn(Code, "fromAsset").mockReturnValue({',
        "    isInline: false,",
        "    bind: () => ({",
        "      s3Location: {",
        '        bucketName: "mock-assets-bucket",',
        '        objectKey: "mock-asset-key.zip",',
        "      },",
        "    }),",
        "    bindToResource: () => {},",
        "  } as unknown as AssetCode);",
        "});",
        "",
        "afterAll(() => {",
        "  fromAssetMock.mockRestore();",
        "});",
        "```",
        "",
        "- Normalize the template before snapshotting when the stack includes asset-based Lambdas",
        "- Use `Template.fromStack(stack)` and assert with `hasResourceProperties` for targeted checks",
      ].join("\n"),
      tags: ["infrastructure"],
    },
  ],
};
